import { useMemo, useState } from 'react'

import { API } from '@/api'
import { ServerDomainInfo, Volume } from '@/api/type-server'
import { Button, message, Modal, Trans } from '@/components'
import DriveVolumeQuotaSection from '@/pages/space-mgr/components/drive-volume-quota-section.tsx'
import ImageVolumeQuotaSection from '@/pages/space-mgr/components/image-volume-quota-section.tsx'
import VolumeQuotaSection from '@/pages/space-mgr/components/volume-quota-section'
import { VolumeQuotaSegmentedBar } from '@/pages/space-mgr/components/volume-quota-segmented-bar'
import { formatDatePreset } from '@/utils/formatter'
import { formatGB, getEffectiveQuota } from '@/utils/storage'
import { cn } from '@/utils/style'

export interface SelectedData {
  selectedData: Array<SelectedDataItem>
  lastestVolume?: Volume
}

interface SelectedDataItem {
  volume: Volume
  selectedQuotaInGB: number
}

export default function TeamVolumeQuotaModal(props: {
  open: boolean
  data: {
    snapshotVolumes?: Volume[]
    cloudVolumes?: Volume[]
    imageVolumes?: Volume[]
    domainInfos?: ServerDomainInfo[]
    partVolumeQuotaInGB: number
    isExpired?: boolean
    expireDate?: number | null
  }
  onClose: () => void
  onOk: () => void
}) {
  const { open, data, onClose, onOk } = props
  const {
    snapshotVolumes,
    cloudVolumes,
    imageVolumes,
    domainInfos,
    partVolumeQuotaInGB,
    isExpired,
    expireDate,
  } = data || {}

  const [loading, setLoading] = useState(false)

  const hasImageVolumes = (imageVolumes?.length ?? 0) > 0
  const hasSnapshotVolumes = (snapshotVolumes?.length ?? 0) > 0

  const [summarizedData, setSummarizedData] = useState({
    selectedData: [...(snapshotVolumes || []), ...(cloudVolumes || [])].map((val) => {
      return {
        volume: val,
        selectedQuotaInGB: getEffectiveQuota(val, isExpired ?? false),
      } as SelectedDataItem
    }),
  } as SelectedData)

  const { availableInGB, imageVolumeQuota, hasModified, shouldDisableUpdate } = useMemo(() => {
    const { selectedData = [] } = summarizedData
    let selectedInGB = 0
    for (const item of selectedData) {
      selectedInGB += item.selectedQuotaInGB
    }

    // Calculate Image Volume minimum quota requirement
    const imageVolumeUsage = (imageVolumes || []).reduce((sum, vol) => sum + vol.usage_mb / 1024, 0)
    const imageVolumeMinQuota = Math.ceil(imageVolumeUsage)

    const index = selectedData.findIndex(
      (d) => getEffectiveQuota(d.volume, isExpired ?? false) !== d.selectedQuotaInGB,
    )
    const hasModified = index !== -1

    // Check for warning conditions
    let hasInsufficientQuota = false

    selectedData.forEach((item) => {
      const { volume, selectedQuotaInGB } = item
      const usedGB = Number(((volume.usage_mb || 0) / 1024).toFixed(2))
      const rawUsageBytes = volume.usage_bytes ?? (volume.usage_mb || 0) * 1024 * 1024

      if (rawUsageBytes > 0 && selectedQuotaInGB < Math.max(usedGB, 1)) {
        hasInsufficientQuota = true
      }
    })

    const hasQuotaConflict = selectedInGB + imageVolumeMinQuota > partVolumeQuotaInGB

    const shouldDisableUpdate = hasInsufficientQuota || hasQuotaConflict

    const totalAllocatable = Math.max(0, partVolumeQuotaInGB - imageVolumeMinQuota)
    const availableInGB = Math.max(0, totalAllocatable - selectedInGB)
    const imageVolumeQuota = imageVolumeMinQuota + availableInGB

    return {
      availableInGB,
      imageVolumeQuota,
      hasModified,
      shouldDisableUpdate,
    }
  }, [summarizedData, partVolumeQuotaInGB, imageVolumes, isExpired])

  const onVolumeChange = (volume: Volume, value: number) => {
    const { selectedData = [] } = summarizedData
    const index = selectedData.findIndex((val) => val.volume.id === volume.id)

    if (index !== -1) {
      selectedData[index].selectedQuotaInGB = value
    } else {
      selectedData.push({
        volume: volume,
        selectedQuotaInGB: value,
      })
    }

    summarizedData.lastestVolume = volume
    setSummarizedData({ ...summarizedData, selectedData })
  }

  const onConfirm = async () => {
    const { selectedData = [] } = summarizedData
    const modifiedVolumes = selectedData.filter(
      (d) => getEffectiveQuota(d.volume, isExpired ?? false) !== d.selectedQuotaInGB,
    )

    // Classify operations: decrease quota first, then increase quota
    const decreaseOps = modifiedVolumes.filter(
      (i) => i.selectedQuotaInGB < getEffectiveQuota(i.volume, isExpired ?? false),
    )
    const increaseOps = modifiedVolumes.filter(
      (i) => i.selectedQuotaInGB >= getEffectiveQuota(i.volume, isExpired ?? false),
    )

    const createRequest = (item: SelectedDataItem) => {
      // Check if volume has res_id - if not, it's a placeholder (new volume)
      const volumeAny = item.volume as any
      const hasResId = volumeAny.res_id && volumeAny.res_id !== ''

      if (!hasResId) {
        // Create new team subvolume
        return API.createTeamSubvolume({
          type: item.volume.type || 1,
          domain: item.volume.domain || 0,
          init_quota_gb: item.selectedQuotaInGB,
        })
          .then((response) => {
            return { success: !response || response.code === 0, response }
          })
          .catch((error) => ({ success: false, error }))
      } else {
        // Modify existing team subvolume
        return API.modifyTeamSubvolume({
          res_id: volumeAny.res_id,
          quota_gb: item.selectedQuotaInGB,
        })
          .then((response) => {
            return { success: response.code === 0, response }
          })
          .catch((error) => ({ success: false, error }))
      }
    }

    try {
      setLoading(true)
      let allResults: Array<{ success: boolean; response?: any; error?: any }> = []

      // Phase 1: Execute all decrease operations first
      if (decreaseOps.length > 0) {
        const decreaseRequests = decreaseOps.map(createRequest)
        const decreaseResults = await Promise.all(decreaseRequests)
        allResults = [...decreaseResults]

        const decreaseSuccessCount = decreaseResults.filter((result) => result.success).length

        // Only proceed to Phase 2 if all decrease operations succeeded
        if (decreaseSuccessCount !== decreaseOps.length) {
          const totalCount = decreaseOps.length
          if (decreaseSuccessCount > 0) {
            message.show(
              `Partially successful: ${decreaseSuccessCount}/${totalCount} operations succeeded`,
            )
          } else {
            message.error('Modification failed')
          }
          onOk()
          return
        }
      }

      // Phase 2: Execute all increase operations
      if (increaseOps.length > 0) {
        const increaseRequests = increaseOps.map(createRequest)
        const increaseResults = await Promise.all(increaseRequests)
        allResults = [...allResults, ...increaseResults]
      }

      // Report combined results
      const successCount = allResults.filter((result) => result.success).length
      const totalCount = allResults.length

      if (successCount === totalCount) {
        message.success('Modification successful')
      } else if (successCount > 0) {
        message.show(`Partially successful: ${successCount}/${totalCount} operations succeeded`)
      } else {
        message.error('Modification failed')
      }

      onOk()
    } catch (error) {
      console.error('Error during volume modifications:', error)
      message.error('Modification failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      className="w-[800px]"
      open={open}
      onClose={() => {
        if (!loading) {
          onClose()
        }
      }}
    >
      <Modal.Header>
        <Trans i18nKey="create.volumeQuotaModalTitle">Modify Quota</Trans>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto max-h-[60vh]">
        <div className="t-bold text-primary">
          <Trans i18nKey="space.titleSpaceStorage">Storage Space</Trans>
        </div>

        <div className="my-4">
          <VolumeQuotaSegmentedBar
            summarizedData={summarizedData}
            totalQuota={partVolumeQuotaInGB}
            imageOnSpaceBilling={hasImageVolumes}
            snapshotOnSpaceBilling={hasSnapshotVolumes}
          />
        </div>

        <table className="table">
          <tbody>
            <tr className="h-[44px]">
              <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                <Trans i18nKey="space.volumeQuotaModel.storage">Storage</Trans>
              </td>
              <td
                className={cn(
                  ' t-sm !px-0 !py-3',
                  partVolumeQuotaInGB ? 'text-primary' : 'text-tertiary',
                )}
              >
                {partVolumeQuotaInGB ? formatGB(partVolumeQuotaInGB, { decimals: 0 }) : '-'}
              </td>
            </tr>
            <tr className="h-[44px]">
              <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                <Trans i18nKey="space.volumeQuotaModel.expireOn">Expire</Trans>
              </td>
              <td className={cn('t-sm !px-0 !py-3', expireDate ? 'text-primary' : 'text-tertiary')}>
                {expireDate ? formatDatePreset(expireDate) : '-'}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="mt-4 mb-2 flex flex-col gap-4">
          {hasImageVolumes && <ImageVolumeQuotaSection imageVolumeQuota={imageVolumeQuota} />}
          {hasSnapshotVolumes && (
            <VolumeQuotaSection
              isSnapshot={true}
              volumes={snapshotVolumes || []}
              availableInGB={availableInGB}
              summarizedData={summarizedData}
              onChange={onVolumeChange}
            />
          )}
          {cloudVolumes && (
            <DriveVolumeQuotaSection
              volumes={cloudVolumes}
              availableInGB={availableInGB}
              summarizedData={summarizedData}
              domainInfos={domainInfos}
              onChange={onVolumeChange}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="outline"
          onClick={() => {
            if (!loading) {
              onClose()
            }
          }}
        >
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          disabled={loading || !hasModified || shouldDisableUpdate}
          onClick={onConfirm}
        >
          <Trans i18nKey="common.confirmModalUpdate">Update</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
